// Underwritten returns — every underwritten deal as a dot on one IRR axis.
//
// No hurdle line: the 15% bar came off Pipeline and stays off here. Deals that
// land within a third of a point of each other stack upward instead of
// covering one another, so every deal stays visible and hoverable.
import { stageRamp } from "../common/brand";
import { STAGE_LABEL } from "../../services/homeView";

export default function ReturnsStrip({ returns, bind }) {
  const { underwritten, n, of, medianIrr, minIrr, maxIrr, medianEm } = returns;
  const lo = minIrr != null ? Math.floor(minIrr) - 1 : 0;
  const hi = maxIrr != null ? Math.ceil(maxIrr) + 1 : 1;
  const X = (v) => ((v - lo) / (hi - lo)) * 100;

  const placed = [];
  const dots = underwritten.map((r) => {
    const taken = new Set(placed.filter((p) => Math.abs(p.irr - r.irr) < 0.35).map((p) => p.lift));
    let lift = 0;
    while (taken.has(lift)) lift += 1;
    placed.push({ irr: r.irr, lift });
    return { r, lift };
  });

  return (
    <section data-print-block className="rounded-[14px] border border-[#3a3a3a] bg-[#242424] px-6 py-5">
      <h3 className="text-[15px] font-semibold text-[#f4f4f4]">Underwritten returns</h3>
      <p className="mt-0.5 text-[12px] text-[#8a8a8a]">{n} of {of} deals underwritten</p>

      {n === 0 ? (
        <div className="py-8 text-[12.5px] text-[#8a8a8a]">No live deal carries a return yet.</div>
      ) : (
        <>
          <div className="mt-4 flex gap-7">
            {[
              ["Median levered IRR", `${medianIrr.toFixed(1)}%`],
              ["Range", `${minIrr.toFixed(1)}–${maxIrr.toFixed(1)}%`],
              ["Median multiple", medianEm != null ? `${medianEm.toFixed(2)}x` : "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#8a8a8a]">{k}</div>
                <div className="mt-1 text-[22px] font-light tracking-[-0.01em] text-[#f4f4f4]">{v}</div>
              </div>
            ))}
          </div>

          <div className="relative mx-1 mt-2.5 h-[120px]">
            <div className="absolute inset-x-0 top-16 border-t border-[#3a3a3a]" />
            {Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).map((t) => (
              <div key={t} className="absolute top-[70px] -translate-x-1/2 text-[10.5px] tabular-nums text-[#8a8a8a]" style={{ left: `${X(t)}%` }}>{t}%</div>
            ))}
            <div className="absolute bottom-11 top-[18px] border-l-[1.5px] border-[#f4f4f4]" style={{ left: `${X(medianIrr)}%` }}>
              <span className="absolute -top-4 left-0 -translate-x-1/2 whitespace-nowrap text-[10.5px] font-semibold text-[#f4f4f4]">median {medianIrr.toFixed(1)}%</span>
            </div>
            {dots.map(({ r, lift }) => (
              <div key={r.id}
                {...bind(<><b>{r.name}</b><div className="text-[10.5px] text-[#8a8a8a]">{STAGE_LABEL[r.stage]} · {r.market}</div>{r.irr.toFixed(1)}% levered IRR{r.em != null ? ` · ${r.em.toFixed(2)}x` : ""}</>)}
                className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#242424]"
                style={{ left: `${X(r.irr)}%`, top: 64 - lift * 16, background: stageRamp[r.stage] }} />
            ))}
          </div>
          <p className="text-[10.5px] leading-relaxed text-[#7a7a7a]">
            Levered IRR from each deal's current underwriting. Deals not yet underwritten are excluded, not counted as zero.
          </p>
        </>
      )}
    </section>
  );
}
